import React, { createContext, useState, useRef, useEffect, useCallback } from 'react';
import { createLayerId, pushLayer, popLayer, isTopLayer, getLayerZIndex } from '../Popup/popupStack';

/**
 * Shared context for Dropdown state.
 * Provides `open`, `setOpen`, `triggerRef`, `menuRef`, `handleToggle`, `zIndex`, and `close`
 * to all child components (DropdownTrigger, DropdownMenu, DropdownItem).
 */
export const DropdownContext = createContext();

/**
 * Dropdown — Hover-triggered dropdown.
 *
 * Opens on mouseEnter, closes on mouseLeave. Best for navigation menus
 * or quick-access lists where the user doesn't need to click.
 *
 * Compose with: <DropdownTrigger>, <DropdownMenu>, <DropdownItem>
 */
export const Dropdown = ({ children }) => {
  const [open, setOpen] = useState(false);

  return (
    <DropdownContext.Provider value={{ open, setOpen }}>
      <div
        className="dropdown-root"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        {children}
      </div>
    </DropdownContext.Provider>
  );
};

/**
 * ClickableDropdown — Click-triggered dropdown with stack-aware z-index.
 *
 * Opens on click, closes when clicking outside or pressing Escape (only when
 * this is the topmost layer, so a popup's Escape doesn't bleed through).
 * Registers with popupStack so its z-index is always above any open popup.
 *
 * @param {boolean} showLabel  - Show a text label beside the trigger
 * @param {string}  label      - Label text
 * @param {string}  labelClass - CSS class for the label
 * @param {func}    onOpen     - Called when the dropdown opens
 */
export const ClickableDropdown = ({ children, showLabel = false, label = "", labelClass = "dropdown-label body-s-semibold", labelStyle = {}, onOpen, rootStyle = {}, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const layerIdRef = useRef(createLayerId());
  const [zIndex, setZIndex] = useState(null);

  const handleClose = useCallback(() => {
    setOpen(false);
    popLayer(layerIdRef.current);
  }, []);

  const handleToggle = useCallback(() => {
    if (open) {
      handleClose();
    } else {
      pushLayer(layerIdRef.current);
      setZIndex(getLayerZIndex(layerIdRef.current));
      onOpen?.();
      setOpen(true);
    }
  }, [open, onOpen, handleClose]);

  // Close on outside click — skip clicks inside the trigger root or the portal menu
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(event.target)) return;
      if (menuRef.current && menuRef.current.contains(event.target)) return;
      // Skip if click is inside any portaled dropdown menu (nested dropdown case)
      if (event.target.closest('.kb-dropdown-menu')) return;
      if (open) handleClose();
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open, handleClose]);

  // Escape closes only the topmost layer
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isTopLayer(layerIdRef.current)) {
        e.stopPropagation();
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleClose]);

  // Safety cleanup on unmount
  useEffect(() => {
    const id = layerIdRef.current;
    return () => popLayer(id);
  }, []);

  return (
    <DropdownContext.Provider value={{
      open,
      setOpen,
      triggerRef: rootRef,
      menuRef,
      handleToggle: disabled ? undefined : handleToggle,
      close: handleClose,
      zIndex: zIndex,
    }}>
      <div className="dropdown-root" ref={rootRef} onClick={disabled ? undefined : handleToggle} style={rootStyle}>
        {showLabel === true && <span className={labelClass} style={labelStyle}>{label}</span>}
        {children}
      </div>
    </DropdownContext.Provider>
  );
};
